import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';

// Cache for academic years data and settings
interface AcademicYearCache {
  years: string[];
  databaseActive: boolean;
  timestamp: number;
  activeYear?: string;
}

let academicYearCache: AcademicYearCache | null = null;
let dynamicYearsCache: { enabled: boolean; timestamp: number } | null = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DATABASE_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes for database data

// Database connectivity flag
let isDatabaseAvailable = true;
let lastDatabaseCheck = 0;
const DATABASE_CHECK_INTERVAL = 30 * 1000; // 30 seconds

/**
 * Calculate adjacent academic years (previous and next year)
 */
export function getAdjacentAcademicYears(baseYear: string): string[] {
  const years = [];
  
  // Parse the base year (e.g., "2025-2026")
  const match = baseYear.match(/^(\d{4})-(\d{4})$/);
  if (!match) {
    console.warn(`Invalid academic year format: ${baseYear}`);
    return [baseYear]; // Return just the base year if format is invalid
  }
  
  const startYear = parseInt(match[1], 10);
  const endYear = parseInt(match[2], 10);
  
  // Previous year: 2024-2025 if base is 2025-2026
  const prevStartYear = startYear - 1;
  const prevEndYear = endYear - 1;
  const previousYear = `${prevStartYear}-${prevEndYear}`;
  
  // Next year: 2026-2027 if base is 2025-2026
  const nextStartYear = startYear + 1;
  const nextEndYear = endYear + 1;
  const nextYear = `${nextStartYear}-${nextEndYear}`;
  
  return [previousYear, baseYear, nextYear];
}

/**
 * Filter available years based on global settings and adjacent year permissions
 */
export function filterYearsForUser(
  allYears: string[],
  globalYear: string | null,
  allowOverride: boolean,
  allowAdjacentYears: boolean
): string[] {
  // If no global year is set or overrides are allowed, return all years
  if (!globalYear || allowOverride) {
    return allYears;
  }
  
  // If adjacent years are not allowed, only return the global year
  if (!allowAdjacentYears) {
    return allYears.filter(year => year === globalYear);
  }
  
  // Adjacent years are allowed, return the global year and its adjacent years
  const adjacentYears = getAdjacentAcademicYears(globalYear);
  return allYears.filter(year => adjacentYears.includes(year));
}

/**
 * Database API functions for academic years
 */
const academicYearApi = {
  async fetchActiveYears(): Promise<string[]> {
    const response = await fetch('/api/v1/academic-years/active', {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Database fetch failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data?.data?.active?.map((year: any) => year.year) || [];
  },

  async fetchCurrentYear(): Promise<string | null> {
    const response = await fetch('/api/v1/academic-years/active', {
      headers: {
        'Cache-Control': 'no-cache',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Database fetch failed: ${response.status}`);
    }
    
    const data = await response.json();
    return data?.data?.current?.year || null;
  }
};

/**
 * Internal function to check database connectivity
 */
const checkDatabaseConnectivity = async (): Promise<boolean> => {
  const now = Date.now();
  if (now - lastDatabaseCheck < DATABASE_CHECK_INTERVAL) {
    return isDatabaseAvailable;
  }
  
  try {
    const response = await fetch('/api/v1/academic-years?status=active', {
      method: 'HEAD',
      signal: AbortSignal.timeout(3000), // 3 second timeout
    });
    isDatabaseAvailable = response.ok;
    lastDatabaseCheck = now;
    
    if (isDatabaseAvailable) {
      console.log('[Academic Year Utils] Database connectivity restored');
    }
  } catch (error) {
    isDatabaseAvailable = false;
    lastDatabaseCheck = now;
    console.warn('[Academic Year Utils] Database unavailable, using fallback:', error);
  }
  
  return isDatabaseAvailable;
};

/**
 * Async version: Returns an array of academic years from database with fallback
 * Format: "2023-2024", "2024-2025", etc.
 */
export const getAcademicYearsAsync = async (count: number = 5): Promise<string[]> => {
  try {
    // Check cache first
    if (academicYearCache && 
        (Date.now() - academicYearCache.timestamp < DATABASE_CACHE_DURATION) &&
        academicYearCache.databaseActive) {
      return academicYearCache.years;
    }

    // Check database connectivity
    const databaseAvailable = await checkDatabaseConnectivity();
    
    if (databaseAvailable) {
      try {
        const databaseYears = await academicYearApi.fetchActiveYears();
        
        if (databaseYears.length > 0) {
          // Cache successful database result
          academicYearCache = {
            years: databaseYears,
            databaseActive: true,
            timestamp: Date.now(),
          };
          
          console.log('[Academic Year Utils] Successfully fetched from database:', databaseYears);
          return databaseYears;
        }
      } catch (dbError) {
        console.warn('[Academic Year Utils] Database fetch failed:', dbError);
        isDatabaseAvailable = false;
      }
    }
  } catch (error) {
    console.warn('[Academic Year Utils] Database check failed:', error);
  }

  // Fallback to existing logic
  console.log('[Academic Year Utils] Using fallback logic');
  return getAcademicYearsFallback(count);
};

/**
 * Synchronous version (backward compatible): Returns academic years with cache
 * Attempts to use cached database data, falls back to hardcoded values
 */
export const getAcademicYears = (count: number = 5): string[] => {
  // Check if we have fresh database cache
  if (academicYearCache && 
      (Date.now() - academicYearCache.timestamp < DATABASE_CACHE_DURATION) &&
      academicYearCache.databaseActive) {
    return academicYearCache.years;
  }
  
  // Check if we have slightly stale database cache but database was working
  if (academicYearCache && 
      academicYearCache.databaseActive && 
      (Date.now() - academicYearCache.timestamp < CACHE_DURATION)) {
    // Use stale cache but trigger async refresh in background
    setTimeout(() => {
      getAcademicYearsAsync(count).catch(err => {
      console.warn('Failed to async refresh academic years:', err);
      EnhancedErrorLogger.getInstance().logQuickError(err, 'AcademicYearUtils', { context: 'async refresh academic years' });
    });
    }, 0);
    return academicYearCache.years;
  }
  
  // Fallback to existing logic
  return getAcademicYearsFallback(count);
};

/**
 * Internal fallback function that maintains original logic
 */
const getAcademicYearsFallback = (count: number = 5): string[] => {
  // Check localStorage for dynamic years setting
  const savedSetting = localStorage.getItem('dynamicYearsEnabled');
  const useDynamicYears = savedSetting === 'true';
  
  if (useDynamicYears) {
    const dynamicYears = getDynamicAcademicYears(count);
    return dynamicYears;
  }
  
  // Default: return static years for backward compatibility
  const allYears = [
    "2010-2011",
    "2011-2012",
    "2022-2023",
    "2023-2024",
    "2024-2025",
    "2025-2026",
    "2026-2027",
    "2027-2028"
  ];
  
  // Return all years instead of limiting to count for backward compatibility
  return allYears;
};

/**
 * Generates academic years dynamically based on current date
 * Shows 2 years before current, current year, and future years
 */
export const getDynamicAcademicYears = (count: number = 5): string[] => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11
  const currentYear = currentDate.getFullYear();
  
  // Determine current academic year start
  // If we're in July or later, we're in the next academic year
  const currentAcademicYearStart = currentMonth >= 6 ? currentYear : currentYear - 1;
  
  // Calculate how many years to show before and after
  const yearsBefore = 2; // Show 2 previous years
  
  const years: string[] = [];
  const startYear = currentAcademicYearStart - yearsBefore;
  
  // Generate the years
  for (let i = 0; i < count; i++) {
    const year = startYear + i;
    years.push(`${year}-${year + 1}`);
  }
  
  return years;
};

/**
 * Returns an array of available academic years for use in application forms
 * Alias for getAcademicYears to maintain compatibility
 */
export const getApplicationYears = getAcademicYears;

/**
 * Returns available academic years for the school dashboard
 */
export const getAvailableAcademicYears = getAcademicYears;

/**
 * Async version: Returns the current academic year from database with fallback
 */
export const getCurrentApplicationYearAsync = async (): Promise<string> => {
  try {
    // Check cache first
    if (academicYearCache &&
        academicYearCache.activeYear &&
        (Date.now() - academicYearCache.timestamp < DATABASE_CACHE_DURATION) &&
        academicYearCache.databaseActive) {
      return academicYearCache.activeYear;
    }

    // Check database connectivity
    const databaseAvailable = await checkDatabaseConnectivity();
    
    if (databaseAvailable) {
      try {
        const currentYear = await academicYearApi.fetchCurrentYear();
        
        if (currentYear) {
          // Update cache with active year
          if (academicYearCache) {
            academicYearCache.activeYear = currentYear;
            academicYearCache.timestamp = Date.now();
          } else {
            academicYearCache = {
              years: [],
              databaseActive: true,
              timestamp: Date.now(),
              activeYear: currentYear,
            };
          }
          
          console.log('[Academic Year Utils] Current year from database:', currentYear);
          return currentYear;
        }
      } catch (dbError) {
        console.warn('[Academic Year Utils] Failed to fetch current year from database:', dbError);
        isDatabaseAvailable = false;
      }
    }
  } catch (error) {
    console.warn('[Academic Year Utils] Database check failed for current year:', error);
  }

  // Fallback to date calculation
  console.log('[Academic Year Utils] Using date calculation fallback for current year');
  return getCurrentApplicationYearFallback();
};

/**
 * Synchronous version (backward compatible): Returns the current academic year
 */
export const getCurrentApplicationYear = (): string => {
  // Check if we have fresh database cache
  if (academicYearCache &&
      academicYearCache.activeYear &&
      (Date.now() - academicYearCache.timestamp < DATABASE_CACHE_DURATION) &&
      academicYearCache.databaseActive) {
    return academicYearCache.activeYear;
  }
  
  // Check if we have slightly stale database cache
  if (academicYearCache &&
      academicYearCache.activeYear &&
      academicYearCache.databaseActive &&
      (Date.now() - academicYearCache.timestamp < CACHE_DURATION)) {
    // Use stale cache but trigger async refresh in background
    setTimeout(() => {
      getCurrentApplicationYearAsync().catch(err => {
        console.warn('Failed to async refresh current application year:', err);
        EnhancedErrorLogger.getInstance().logQuickError(err, 'AcademicYearUtils', { context: 'async refresh current application year' });
      });
    }, 0);
    return academicYearCache.activeYear;
  }
  
  // Fallback to date calculation
  return getCurrentApplicationYearFallback();
};

/**
 * Internal fallback function for current APPLICATION year calculation
 * Uses October-September application period (NOT July-June school year)
 */
const getCurrentApplicationYearFallback = (): string => {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth(); // 0-11 (0=Jan, 9=Oct)
  const currentYear = currentDate.getFullYear();

  // Application period runs October to September
  // Applications submitted Oct-Sept are for the FOLLOWING school year
  // Example: Oct 2025 through Sept 2026 → applications for 2026-2027 school year

  // If we're in October-December (month >= 9), applications are for next school year
  // Example: October 2025 (month 9) -> 2026-2027
  if (currentMonth >= 9) {
    return `${currentYear + 1}-${currentYear + 2}`;
  }

  // If we're in January-September (month < 9), applications are for the school year starting this year
  // Example: January 2026 (month 0) -> 2026-2027
  return `${currentYear}-${currentYear + 1}`;
};

/**
 * Alias for getCurrentApplicationYear for consistency
 */
export const getCurrentAcademicYear = getCurrentApplicationYear;

/**
 * Returns the next academic year after the current one
 */
export const getNextAcademicYear = (): string => {
  // Get current year and calculate next
  const currentYear = getCurrentApplicationYear();
  const [startYear] = currentYear.split('-').map(Number);
  const nextStart = startYear + 1;
  return `${nextStart}-${nextStart + 1}`;
};

/**
 * Formats an academic year for display
 * Example: "2023-2024" becomes "2023-2024 School Year"
 */
export const formatAcademicYearDisplay = (academicYear: string): string => {
  return `${academicYear} School Year`;
};

/**
 * Validates if a string is a valid academic year in YYYY-YYYY format
 */
export const isValidAcademicYear = (academicYear: string): boolean => {
  const regex = /^\d{4}-\d{4}$/;
  
  if (!regex.test(academicYear)) return false;
  
  const [startYear, endYear] = academicYear.split('-').map(Number);
  return endYear === startYear + 1;
};

/**
 * Cache management functions
 */
export const prefetchAcademicYears = async (): Promise<void> => {
  try {
    await getAcademicYearsAsync();
    await getCurrentApplicationYearAsync();
  } catch (error) {
    console.warn('[Academic Year Utils] Prefetch failed:', error);
  }
};

export const clearAcademicYearCache = (): void => {
  academicYearCache = null;
  console.log('[Academic Year Utils] Cache cleared');
};

export const getAcademicYearCacheStatus = () => {
  return {
    cached: !!academicYearCache,
    databaseActive: academicYearCache?.databaseActive || false,
    age: academicYearCache ? Date.now() - academicYearCache.timestamp : 0,
    isDatabaseAvailable,
    lastDatabaseCheck: Date.now() - lastDatabaseCheck,
  };
};

/**
 * Sets the default academic year for the system
 * Used in SystemSettings.tsx
 */
export const setDefaultAcademicYear = async (year: string): Promise<void> => {
  // Clear cache to force refresh with new default
  clearAcademicYearCache();

  // Write the keys the app actually reads, and clear the legacy one.
  // This used to set `defaultAcademicYear`, the key AcademicYearContext treats
  // as a leftover to migrate away from — so every settings save re-created the
  // thing the migration had just removed, and a day later the stale value was
  // promoted back over the current one. Saving settings is exactly when the
  // canonical value should be written instead.
  // Each write stands alone. Sharing one try meant a stored `systemSettings` of
  // "null" — truthy, so it parses, then throws on property access — skipped the
  // cache write AND the legacy removal, silently restoring the very bug this
  // exists to fix while the admin saw "settings saved".
  try {
    const raw = localStorage.getItem('systemSettings');
    const parsed = raw ? JSON.parse(raw) : {};
    const settings = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    localStorage.setItem(
      'systemSettings',
      JSON.stringify({
        ...settings,
        schoolYears: { ...(settings.schoolYears ?? {}), currentSchoolYear: year },
      }),
    );
  } catch {
    // Unreadable or unwritable settings must not stop the rest.
  }

  try {
    localStorage.setItem('cached_current_academic_year', year);
    localStorage.setItem('cached_current_academic_year_timestamp', Date.now().toString());
  } catch {
    // Storage full: the settings save itself still succeeded.
  }

  try {
    localStorage.removeItem('defaultAcademicYear');
  } catch {
    // Best effort; the read path also removes it when it migrates.
  }
  return Promise.resolve();
};

/**
 * Migration support: Export async versions with clear naming
 * These can be used by components that want to migrate to async patterns
 */
export {
  getAcademicYearsAsync as getAcademicYearsFromDatabase,
  getCurrentApplicationYearAsync as getCurrentApplicationYearFromDatabase,
};

/**
 * Utility function for components migrating from sync to async
 * Returns a promise that resolves with academic years from database
 */
export const migrateToAsyncAcademicYears = async (count?: number): Promise<string[]> => {
  return await getAcademicYearsAsync(count);
};

/**
 * Auto-initialization function for apps that want to preload data
 * Call this in your app's startup (e.g., _app.tsx or App.tsx)
 */
export const initializeAcademicYears = async (): Promise<void> => {
  try {
    console.log('[Academic Year Utils] Initializing...');
    await prefetchAcademicYears();
    console.log('[Academic Year Utils] Initialized successfully');
  } catch (error) {
    console.warn('[Academic Year Utils] Initialization failed, will use fallbacks:', error);
  }
};
